import type { ComboboxLabels } from "@controls";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * Translated UI strings for the shared {@link Combobox} / {@link Select}
 * controls. Provided app-wide via `ControlLabelsProvider`; spread into a
 * `labels` prop only when a single call site needs to override them.
 */
export function useComboboxLabels(): Partial<ComboboxLabels> {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      selectAll: t("controls.combobox.selectAll"),
      deselectAll: t("controls.combobox.deselectAll"),
      clear: t("controls.combobox.clear"),
      selectedCount: (count) => t("controls.combobox.selected", { count }),
      more: (count) => t("controls.combobox.more", { count }),
      showLess: t("controls.combobox.showLess"),
      empty: t("controls.combobox.empty"),
      searchPlaceholder: t("controls.combobox.searchPlaceholder"),
      create: t("controls.combobox.create"),
      unavailable: t("controls.combobox.unavailable"),
      loading: t("controls.combobox.loading"),
      clearAll: t("controls.combobox.clearAll"),
      removeOption: (label) =>
        t("controls.combobox.removeOption", {
          label,
          interpolation: { escapeValue: false },
        }),
      optionRemoved: (label) =>
        t("controls.combobox.optionRemoved", {
          label,
          interpolation: { escapeValue: false },
        }),
      selectionCleared: t("controls.combobox.selectionCleared"),
      resultsAvailable: (count) =>
        count === 1
          ? t("controls.combobox.resultOne")
          : t("controls.combobox.resultMany", { count }),
      createOptionName: (label) =>
        t("controls.combobox.createOption", {
          label,
          interpolation: { escapeValue: false },
        }),
      created: (label) =>
        t("controls.combobox.created", {
          label,
          interpolation: { escapeValue: false },
        }),
    }),
    [t],
  );
}
